import React from 'react';
import { Box, Card, CardContent, Typography, Chip } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getEntityTypeName, getEntityTypeColors, getEntityRoleName } from '../../../constants/managerHistory';
import { calculateDuration } from '../../../utils/dateUtils';

dayjs.extend(utc);

const ResponsibilityCard = ({ responsibility }) => {
  const { entity, type, startDate, endDate, isCurrent, relatedEntities } = responsibility;
  const typeColors = getEntityTypeColors(type);
  const roleName = getEntityRoleName(type);

  // Build line 2 with type name and related entities
  const buildLine2 = () => {
    const parts = [getEntityTypeName(type)];

    if (relatedEntities?.cluster) {
      parts.push(relatedEntities.cluster.name);
    }

    if (relatedEntities?.businessUnit) {
      parts.push(relatedEntities.businessUnit.name);
    }

    if (relatedEntities?.project) {
      parts.push(relatedEntities.project.name);
    }

    return parts.join(' • ');
  };

  // Build date range with duration
  const buildDateRange = () => {
    const start = dayjs.utc(startDate).format('DD/MM/YYYY');
    const end = endDate ? dayjs.utc(endDate).format('DD/MM/YYYY') : 'Present';
    const duration = calculateDuration(startDate, endDate);

    return `${start} - ${end}  •  ${duration}`;
  };

  const line2Text = buildLine2();

  return (
    <Card
      sx={{
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        width: '100%',
        border: '1px solid #e0e0e0',
        boxShadow: 'none'
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Line 1: Role chip (colored) + Active/Inactive status */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          mb: 2
        }}>
          {roleName && (
            <Chip
              label={roleName}
              size="small"
              sx={{
                height: '20px',
                fontSize: '11px',
                fontWeight: 500,
                backgroundColor: typeColors.bg,
                color: typeColors.text,
                flexShrink: 0,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
          )}
          <Chip
            label={isCurrent ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              height: '20px',
              fontSize: '11px',
              fontWeight: 500,
              backgroundColor: isCurrent ? '#E8F5E9' : '#F3F4F6',
              color: isCurrent ? '#2E7D32' : '#6B7280',
              flexShrink: 0,
              '& .MuiChip-label': {
                px: 1
              }
            }}
          />
        </Box>

        {/* Line 2: Entity name */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mb: 1,
            pl: 1.5
          }}
        >
          {entity?.name || `Unknown ${getEntityTypeName(type)}`}
        </Typography>

        {/* Line 3: Type + Breadcrumb hierarchy as plain text */}
        <Typography
          variant="body2"
          sx={{
            color: '#64748B',
            fontSize: '13px',
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            pl: 1.5
          }}
        >
          {line2Text}
        </Typography>

        {/* Line 4: Date range with duration */}
        <Typography
          variant="body2"
          sx={{
            color: '#64748B',
            fontSize: '13px',
            pl: 1.5
          }}
        >
          {buildDateRange()}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default ResponsibilityCard;
